// pages/admin/program/ProgramRegistrations.tsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, Timestamp, writeBatch, runTransaction, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { ProgramRegistration, programService, ProgramBeneficiary } from '../../../firebase/programFirestoreService';
import { FaSearch, FaFilter, FaUserCheck, FaUserTimes, FaDownload, FaEnvelope, FaPrint, FaPlus, FaEdit } from '../../../utils/iconUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { useUI } from '../../../components/common/UIMessageProvider';

const EDIT_REGISTRATION_STORAGE_PREFIX = 'admin-program-registration-edit';

const ProgramRegistrations: React.FC = () => {
  const [registrations, setRegistrations] = useState<ProgramRegistration[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<ProgramRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState<Array<{ id: string; title: string }>>([]);
  const [programSpecificRegistrations, setProgramSpecificRegistrations] = useState<ProgramRegistration[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [updatingAttendance, setUpdatingAttendance] = useState<string | null>(null);
  
  const auth = useAuth();
  const ui = useUI();
  const canExport = auth?.canPerformAction('program', 'export');
  const canPrint = auth?.canPerformAction('program', 'print');
  const canDelete = auth?.canPerformAction('program', 'delete');
  const canEdit = auth?.canPerformAction('program', 'edit');
  
  // Filters
  const [selectedProgram, setSelectedProgram] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [selectedHealth, setSelectedHealth] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('registrationDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [bulkCount, setBulkCount] = useState<number>(5);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  // Import from other program modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [sourceProgramId, setSourceProgramId] = useState<string>('');
  const [sourceRegistrations, setSourceRegistrations] = useState<ProgramBeneficiary[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [loadingSourceRegs, setLoadingSourceRegs] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  // Add selected registrants to another program modal state
  const [showAddToProgramModal, setShowAddToProgramModal] = useState(false);
  const [targetProgramId, setTargetProgramId] = useState<string>('');
  const [addingToProgram, setAddingToProgram] = useState(false);

  useEffect(() => {
    loadPrograms();
    loadRegistrations();
  }, []);

  useEffect(() => {
    // placeholder: actual filtering is handled by consolidated effect below
  }, [registrations, selectedStatus, searchTerm, selectedGender]);

  // When filter or sort criteria change, reset to page 1 and re-apply filters
  useEffect(() => {
    setCurrentPage(1);
    applyFilters();
  }, [selectedProgram, selectedStatus, searchTerm, selectedGender, selectedHealth, sortField, sortDirection]);

  // When underlying registration data updates (e.g. mark attendance, edit),
  // re-apply filters but DO NOT reset the current page so the user stays
  // on the page they were viewing.
  useEffect(() => {
    applyFilters();
  }, [registrations, programSpecificRegistrations]);

  useEffect(() => {
    const fetchForProgram = async (programId: string) => {
      setLoading(true);
      try {
        await refreshProgramRegistrationsForSelectedProgram(programId);
      } catch (err) {
        console.error('Error fetching program registrations for program:', programId, err);
        setProgramSpecificRegistrations([]);
      } finally {
        setLoading(false);
      }
    };

    if (selectedProgram && selectedProgram !== 'all') {
      fetchForProgram(selectedProgram);
    } else if (selectedProgram === 'all') {
      // clear any program-specific cache when viewing all
      setProgramSpecificRegistrations([]);
      applyFilters();
    }
  }, [selectedProgram, programs]);

  useEffect(() => {
    setSelectedIds([]);
  }, [selectedProgram]);

  // Fetch registrations for a selected source program (for import modal)
  useEffect(() => {
    const fetchSource = async (programId: string) => {
      if (!programId) return;
      setLoadingSourceRegs(true);
      try {
        const regs = await programService.getProgramRegistrations(programId, 100).catch(() => [] as ProgramBeneficiary[]);
        setSourceRegistrations(regs);
      } catch (err) {
        console.error('Error fetching source registrations:', err);
        setSourceRegistrations([]);
      } finally {
        setLoadingSourceRegs(false);
      }
    };

    if (sourceProgramId) fetchSource(sourceProgramId);
  }, [sourceProgramId]);

  const mapBeneficiaryToRegistration = (programId: string, beneficiary: ProgramBeneficiary) => ({
    id: beneficiary.id,
    beneficiaryId: beneficiary.beneficiaryId,
    programId,
    programTitle: programs.find(p => p.id === programId)?.title || '',
    idNumber: beneficiary.idNumber,
    idType: beneficiary.idType,
    fullName: beneficiary.fullName,
    gender: beneficiary.gender,
    religion: (beneficiary as any).religion || '',
    contactNumber: beneficiary.contactNumber,
    houseNumber: (beneficiary as any).houseNumber || '',
    address: beneficiary.address,
    email: beneficiary.email,
    maritalStatus: (beneficiary as any).maritalStatus || (beneficiary as any).marital || '',
    healthStatus: (beneficiary as any).healthStatus || (beneficiary as any).health || '',
    status: (beneficiary.registrationStatus || 'confirmed') as ProgramRegistration['status'],
    registrationDate: beneficiary.registrationDate as any,
    dateOfBirth: (beneficiary as any).dateOfBirth || null,
    age: (beneficiary as any).age || undefined,
    emergencyContact: beneficiary.emergencyContact,
    specialRequirements: beneficiary.specialRequirements,
    attended: beneficiary.attended,
    attendanceDate: beneficiary.attendanceDate,
    userId: beneficiary.registeredBy,
    userEmail: beneficiary.registeredByEmail,
    createdAt: beneficiary.createdAt,
    updatedAt: beneficiary.updatedAt
  } as ProgramRegistration & { beneficiaryId?: string });

  const refreshProgramRegistrationsForSelectedProgram = async (programId: string) => {
    // Same approach as ProgramReport: read from program subcollection registrations
    const subRegs = await programService.getProgramRegistrations(programId, 300).catch(() => [] as ProgramBeneficiary[]);
    const mappedSub = subRegs.map(reg => mapBeneficiaryToRegistration(programId, reg));

    // Legacy fallback only when subcollection is empty
    if (mappedSub.length === 0) {
      const legacyRegs = await programService.getLegacyProgramRegistrations(programId).catch(() => [] as ProgramRegistration[]);
      setProgramSpecificRegistrations(legacyRegs.map((r: any) => ({
        ...r,
        houseNumber: r?.houseNumber || '',
        religion: r?.religion || '',
        dateOfBirth: r?.dateOfBirth || null,
        age: r?.age || undefined
      })));
      return;
    }

    setProgramSpecificRegistrations(mappedSub);
  };

  const normalizePhone = (phone = ''): string => phone.replace(/\D/g, '');

  const loadPrograms = async () => {
    try {
      const programsRef = collection(db, 'programs');
      const querySnapshot = await getDocs(query(programsRef, where('isDeleted', '==', 0), firestoreLimit(100)));
      const programList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title
      }));
      setPrograms(programList);
    } catch (error) {
      console.error('Error loading programs:', error);
      ui.showMessage('Failed to load programs', { type: 'error' });
    }
  };

  const loadRegistrations = async () => {
    setLoading(true);
    try {
      // Same approach as ProgramReport for "All Programs":
      // aggregate registrations from each program subcollection.
      const programsRef = collection(db, 'programs');
      const programsSnapshot = await getDocs(query(programsRef, where('isDeleted', '==', 0), firestoreLimit(100)));
      const allPrograms = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        title: (doc.data() as any).title || ''
      }));

      const subcollectionArrays = await Promise.all(
        allPrograms.map(async (program) => {
          const regs = await programService.getProgramRegistrations(program.id, 25).catch(() => [] as ProgramBeneficiary[]);
          return regs.map((r) => ({
            ...mapBeneficiaryToRegistration(program.id, r),
            programTitle: program.title || (r as any).programTitle || ''
          } as ProgramRegistration));
        })
      );
      const subcollectionRegs = subcollectionArrays.flat();
      // Legacy fallback only when there are no subcollection records at all
      if (subcollectionRegs.length === 0) {
        const registrationsRef = collection(db, 'programRegistrations');
        const legacySnapshot = await getDocs(query(registrationsRef, firestoreLimit(500)));
        const legacyRegs = legacySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          programTitle: (doc.data() as any).programTitle || allPrograms.find(p => p.id === (doc.data() as any).programId)?.title || '',
          houseNumber: (doc.data() as any).houseNumber || '',
          religion: (doc.data() as any).religion || '',
          maritalStatus: (doc.data() as any).maritalStatus || (doc.data() as any).marital || '',
          healthStatus: (doc.data() as any).healthStatus || (doc.data() as any).health || '',
          dateOfBirth: (doc.data() as any).dateOfBirth || null,
          age: (doc.data() as any).age || undefined
        } as any as ProgramRegistration));
        setRegistrations(legacyRegs);
        return;
      }

      setRegistrations(subcollectionRegs);
    } catch (error) {
      console.error('Error loading registrations:', error);
      ui.showMessage('Failed to load registrations', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const base = selectedProgram === 'all' ? registrations : programSpecificRegistrations;
    let filtered = [...(base || [])];

    if (selectedStatus !== 'all') {
      if (selectedStatus === 'attended') {
        filtered = filtered.filter(reg => !!reg.attended);
      } else if (selectedStatus === 'unattended') {
        filtered = filtered.filter(reg => !reg.attended);
      } else {
        filtered = filtered.filter(reg => reg.status === selectedStatus);
      }
    }

    if (selectedGender !== 'all') {
      filtered = filtered.filter(reg => reg.gender === selectedGender);
    }

    if (selectedHealth !== 'all') {
      filtered = filtered.filter(reg => {
        const h = ((reg as any).healthStatus || (reg as any).health || '').toString().toLowerCase();
        return h === selectedHealth;
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(reg =>
        (reg.fullName || '').toLowerCase().includes(term) ||
        (reg.email || '').toLowerCase().includes(term) ||
        (reg.contactNumber || '').toLowerCase().includes(term) ||
        (reg.address || '').toLowerCase().includes(term) ||
        ((reg as any).religion || '').toString().toLowerCase().includes(term)
      );
    }

    filtered = applySorting(filtered);
    setFilteredRegistrations(filtered);
  };

  const applySorting = (data: ProgramRegistration[]) => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';

      switch (sortField) {
        case 'fullName':
          aVal = (a.fullName || '').toLowerCase();
          bVal = (b.fullName || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'contactNumber':
          aVal = a.contactNumber || '';
          bVal = b.contactNumber || '';
          break;
        case 'address':
          aVal = (a.address || '').toLowerCase();
          bVal = (b.address || '').toLowerCase();
          break;
        case 'gender':
          aVal = (a.gender || '').toLowerCase();
          bVal = (b.gender || '').toLowerCase();
          break;
        case 'religion':
          aVal = ((a as any).religion || '').toLowerCase();
          bVal = ((b as any).religion || '').toLowerCase();
          break;
        case 'healthStatus':
          aVal = ((a as any).healthStatus || (a as any).health || '').toLowerCase();
          bVal = ((b as any).healthStatus || (b as any).health || '').toLowerCase();
          break;
        case 'maritalStatus':
          aVal = ((a as any).maritalStatus || (a as any).marital || '').toLowerCase();
          bVal = ((b as any).maritalStatus || (b as any).marital || '').toLowerCase();
          break;
        case 'status':
          aVal = (a.status || '').toLowerCase();
          bVal = (b.status || '').toLowerCase();
          break;
        case 'attendanceDate':
          aVal = a.attendanceDate?.toDate?.()?.getTime() || 0;
          bVal = b.attendanceDate?.toDate?.()?.getTime() || 0;
          break;
        case 'registrationDate':
        default:
          aVal = a.registrationDate?.toDate?.()?.getTime() || 0;
          bVal = b.registrationDate?.toDate?.()?.getTime() || 0;
          break;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setSelectedStatus(value);

    // In attended view, default sorting should show latest attendance first.
    if (value === 'attended') {
      setSortField('attendanceDate');
      setSortDirection('desc');
      return;
    }

    // Restore date-based sorting when leaving attended view.
    if (sortField === 'attendanceDate') {
      setSortField('registrationDate');
      setSortDirection('desc');
    }
  };

  const getSortIndicator = (field: string) => {
    if (sortField !== field) return <span className="ml-1 text-gray-400">⇅</span>;
    return <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(filteredRegistrations.length / pageSize));
  const paginatedRegistrations = filteredRegistrations.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const showingStart = filteredRegistrations.length ? (currentPage - 1) * pageSize + 1 : 0;
  const showingEnd = Math.min(currentPage * pageSize, filteredRegistrations.length);

  const updateRegistrationStatus = async (registrationId: string, newStatus: ProgramRegistration['status']) => {
    try {
      const registrationRef = doc(db, 'programRegistrations', registrationId);
      await updateDoc(registrationRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      setRegistrations(prev =>
        prev.map(reg =>
          reg.id === registrationId ? { ...reg, status: newStatus } : reg
        )
      );
      
      ui.showMessage(`Registration ${newStatus} successfully`, { type: 'success' });
    } catch (error) {
      console.error('Error updating status:', error);
      ui.showMessage('Failed to update status', { type: 'error' });
    }
  };

  const markAttendanceInLocalState = (registrationIds: string[]) => {
    const idSet = new Set(registrationIds);
    const now = Timestamp.now();
    const markLocal = (reg: ProgramRegistration): ProgramRegistration => {
      if (reg.id && idSet.has(reg.id)) {
        return {
          ...reg,
          attended: true,
          attendanceDate: now,
          status: 'confirmed'
        };
      }
      return reg;
    };

    setRegistrations(prev => prev.map(markLocal));
    setProgramSpecificRegistrations(prev => prev.map(markLocal));
    setFilteredRegistrations(prev => prev.map(markLocal));
  };

  const handleMarkAttendance = async (registration: ProgramRegistration & { beneficiaryId?: string }) => {
    if (!registration.id) {
      ui.showMessage('Invalid registration ID', { type: 'error' });
      return;
    }

    setUpdatingAttendance(registration.id);
    
    try {
      // Check if this is from the new subcollection structure (has beneficiaryId + programId)
      if (registration.beneficiaryId && registration.programId) {
        const result = await programService.markAttendance(registration.programId, registration.beneficiaryId);
        
        if (result.success) {
          markAttendanceInLocalState([registration.id]);
          ui.showMessage('Attendance marked successfully', { type: 'success' });
        } else {
          let errText = 'Failed to mark attendance';
          if (typeof result.error === 'string') {
            errText = result.error;
          } else if (result.error && typeof (result.error as any).message === 'string') {
            errText = (result.error as any).message;
          } else if (result.error) {
            try {
              errText = JSON.stringify(result.error);
            } catch (e) {
              // ignore
            }
          }
          console.error('markAttendance error:', result.error);
          ui.showMessage(errText, { type: 'error' });
        }
      } else {
        // Legacy collection update
        const registrationRef = doc(db, 'programRegistrations', registration.id);
        await updateDoc(registrationRef, {
          attended: true,
          attendanceDate: serverTimestamp(),
          status: 'confirmed',
          updatedAt: serverTimestamp()
        });

        markAttendanceInLocalState([registration.id]);
        
        ui.showMessage('Attendance marked successfully', { type: 'success' });
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      ui.showMessage('Failed to mark attendance', { type: 'error' });
    } finally {
      setUpdatingAttendance(null);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Address', 'Gender', 'Religion', 'Status', 'Program', 'Registration Date', 'Attended'];
    const csvData = filteredRegistrations.map(reg => [
      reg.fullName,
      reg.email,
      reg.contactNumber,
      reg.address || '',
      reg.gender,
      (reg as any).religion || '',
      reg.status,
      reg.programTitle,
      reg.registrationDate?.toDate?.()?.toLocaleDateString() || 'N/A',
      reg.attended ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `registrations_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openImportModal = () => {
    if (!selectedProgram || selectedProgram === 'all') {
      ui.showMessage('Please select a target program first', { type: 'info' });
      return;
    }
    setSourceProgramId('');
    setSourceRegistrations([]);
    setSelectedSourceIds([]);
    setShowImportModal(true);
  };

  const toggleSelectSource = (id: string) => {
    setSelectedSourceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleImportSelected = async () => {
    if (!selectedProgram || selectedProgram === 'all') {
      ui.showMessage('Select a target program first', { type: 'error' });
      return;
    }
    if (selectedSourceIds.length === 0) {
      ui.showMessage('No participants selected to import', { type: 'info' });
      return;
    }

    setImportLoading(true);
    let added = 0, skipped = 0, failed = 0;

    for (const id of selectedSourceIds) {
      const src = sourceRegistrations.find(r => r.id === id);
      if (!src) continue;

      try {
        // Skip duplicate by idNumber when available
        if (src.idNumber) {
          const exists = await programService.checkDuplicateId(selectedProgram, src.idNumber).catch(() => false);
          if (exists) {
            skipped++;
            continue;
          }
        }

        const beneficiaryData: any = {
          programTitle: programs.find(p => p.id === selectedProgram)?.title || '',
          idNumber: src.idNumber,
          idType: src.idType,
          fullName: src.fullName,
          gender: src.gender,
          religion: (src as any).religion || undefined,
          contactNumber: src.contactNumber,
          address: src.address,
          email: src.email,
          maritalStatus: (src as any).maritalStatus || (src as any).marital,
          healthStatus: (src as any).healthStatus || (src as any).health,
          dateOfBirth: (src as any).dateOfBirth || undefined,
          age: (src as any).age || undefined,
          registrationStatus: (src as any).registrationStatus || 'confirmed',
          specialRequirements: src.specialRequirements,
          emergencyContact: src.emergencyContact,
          registeredBy: auth?.currentUser?.uid,
          registeredByEmail: auth?.currentUser?.email
        };

        // Remove undefined values to avoid Firestore errors (Firestore rejects undefined)
        Object.keys(beneficiaryData).forEach(key => {
          if (beneficiaryData[key] === undefined) delete beneficiaryData[key];
        });

        const res: any = await programService.registerForProgram(selectedProgram, beneficiaryData).catch(() => ({ success: false }));
        if (res && res.success) added++; else failed++;
      } catch (err) {
        console.error('Error importing participant', id, err);
        failed++;
      }
    }

    // Refresh registrations list
    await loadRegistrations();
    if (selectedProgram && selectedProgram !== 'all') {
      try {
        await refreshProgramRegistrationsForSelectedProgram(selectedProgram);
      } catch (e) {
        console.error('Error refreshing program-specific regs after import', e);
      }
    }

    setImportLoading(false);
    setShowImportModal(false);
    ui.showMessage(`Imported: ${added}. Skipped: ${skipped}. Failed: ${failed}`, { type: failed ? 'warning' : 'success' });
  };

  const getSelectedRegistrations = (): ProgramRegistration[] => {
    const selectedSet = new Set(selectedIds);
    const allRegs = [...registrations, ...programSpecificRegistrations];
    const unique = new Map<string, ProgramRegistration>();
    allRegs.forEach(reg => {
      if (reg.id && selectedSet.has(reg.id) && !unique.has(reg.id)) {
        unique.set(reg.id, reg);
      }
    });
    return Array.from(unique.values());
  };

  const openAddToProgramModal = () => {
    if (!selectedProgram || selectedProgram === 'all') {
      ui.showMessage('Please select the source program first', { type: 'info' });
      return;
    }
    if (selectedIds.length === 0) {
      ui.showMessage('Select one or more members first', { type: 'info' });
      return;
    }

    const selectedRegs = getSelectedRegistrations();
    if (selectedRegs.length === 0) {
      ui.showMessage('No selected members found', { type: 'warning' });
      return;
    }

    const mixedSources = selectedRegs.some(reg => reg.programId !== selectedProgram);
    if (mixedSources) {
      ui.showMessage('Please select members from one source program only', { type: 'warning' });
      return;
    }

    setTargetProgramId('');
    setShowAddToProgramModal(true);
  };

  const closeAddToProgramModal = () => {
    setShowAddToProgramModal(false);
    setTargetProgramId('');
  };

  const handleAddSelectedToProgram = async () => {
    if (!selectedProgram || selectedProgram === 'all') {
      ui.showMessage('Please select the source program first', { type: 'error' });
      return;
    }
    if (!targetProgramId) {
      ui.showMessage('Please select the destination program', { type: 'info' });
      return;
    }
    if (targetProgramId === selectedProgram) {
      ui.showMessage('Destination program must be different from source program', { type: 'warning' });
      return;
    }

    const selectedRegs = getSelectedRegistrations();
    if (selectedRegs.length === 0) {
      ui.showMessage('No selected members found', { type: 'warning' });
      return;
    }

    setAddingToProgram(true);
    let added = 0;
    let skipped = 0;
    let failed = 0;

    try {
      const targetProgramTitle = programs.find(p => p.id === targetProgramId)?.title || '';
      const sourceProgramTitle = programs.find(p => p.id === selectedProgram)?.title || selectedProgram;

      const existingRegs = await programService.getProgramRegistrations(targetProgramId, 500).catch(() => [] as ProgramBeneficiary[]);
      const existingById = new Set(
        existingRegs
          .map(r => (r.idNumber || '').trim().toUpperCase())
          .filter(Boolean)
      );
      const existingByNamePhone = new Set(
        existingRegs.map(r => `${(r.fullName || '').trim().toLowerCase()}|${normalizePhone(r.contactNumber || '')}`)
      );

      for (const src of selectedRegs) {
        try {
          const idNumber = (src.idNumber || '').trim();
          const fullName = (src.fullName || '').trim();
          const contactNumber = (src.contactNumber || '').trim();
          const address = (src.address || '').trim();
          const namePhoneKey = `${fullName.toLowerCase()}|${normalizePhone(contactNumber)}`;

          if (!fullName || !contactNumber || !address) {
            failed++;
            continue;
          }

          if (idNumber && existingById.has(idNumber.toUpperCase())) {
            skipped++;
            continue;
          }
          if (!idNumber && namePhoneKey !== '|' && existingByNamePhone.has(namePhoneKey)) {
            skipped++;
            continue;
          }

          const beneficiaryData: any = {
            programTitle: targetProgramTitle,
            idNumber: idNumber || undefined,
            idType: src.idType || undefined,
            fullName,
            gender: (src.gender || 'male') as any,
            religion: (src as any).religion || undefined,
            contactNumber,
            address,
            email: src.email || '',
            maritalStatus: (src as any).maritalStatus || (src as any).marital || undefined,
            healthStatus: (src as any).healthStatus || (src as any).health || undefined,
            dateOfBirth: (src as any).dateOfBirth || undefined,
            age: (src as any).age || undefined,
            registrationStatus: src.status || 'confirmed',
            emergencyContact: src.emergencyContact || undefined,
            specialRequirements: src.specialRequirements || undefined,
            registrationNotes: `Copied from program ${sourceProgramTitle}`,
            registeredBy: auth?.currentUser?.uid,
            registeredByEmail: auth?.currentUser?.email
          };

          Object.keys(beneficiaryData).forEach(key => {
            if (beneficiaryData[key] === undefined) delete beneficiaryData[key];
          });

          const result: any = await programService.registerForProgram(targetProgramId, beneficiaryData).catch(() => ({ success: false }));
          if (result && result.success) {
            added++;
            if (idNumber) existingById.add(idNumber.toUpperCase());
            if (namePhoneKey !== '|') existingByNamePhone.add(namePhoneKey);
          } else {
            failed++;
          }
        } catch (error) {
          console.error('Error adding selected registration to program:', src, error);
          failed++;
        }
      }

      await loadRegistrations();
      if (selectedProgram && selectedProgram !== 'all') {
        try {
          await refreshProgramRegistrationsForSelectedProgram(selectedProgram);
        } catch (error) {
          console.error('Error refreshing source program regs after add-to-program:', error);
        }
      }

      setSelectedIds([]);
      closeAddToProgramModal();
      ui.showMessage(`Added: ${added}. Skipped: ${skipped}. Failed: ${failed}`, { type: failed ? 'warning' : 'success' });
    } catch (error) {
      console.error('Error in add-to-program flow:', error);
      ui.showMessage('Failed to add selected members to destination program', { type: 'error' });
    } finally {
      setAddingToProgram(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      return [...prev, id];
    });
  };

  const selectAllVisible = () => {
    const visibleIds = paginatedRegistrations.map(r => r.id as string).filter(Boolean);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    if (allVisibleSelected) {
      // remove visible ids
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      // add visible ids
      setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    const ok = await ui.askConfirm({
      title: 'Delete registrations',
      message: `Delete ${selectedIds.length} selected registration(s)? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!ok) return;
    setDeleting(true);
    try {
      // Determine for each selected id whether it belongs to the legacy collection
      // or the program subcollection. We delete from the correct path accordingly.
      const batch = writeBatch(db);
      // find registrations from current local state
      const allRegs = [...registrations, ...programSpecificRegistrations];
      const regsToDelete = selectedIds.map(id => ({ id, record: allRegs.find(r => r.id === id) }));

      regsToDelete.forEach(({ id, record }) => {
        if (record && (record as any).beneficiaryId && record.programId) {
          // subcollection path: /programs/{programId}/registrations/{id}
          const ref = doc(db, 'programs', record.programId, 'registrations', id);
          batch.delete(ref);
        } else {
          // legacy top-level collection
          const ref = doc(db, 'programRegistrations', id);
          batch.delete(ref);
        }
      });

      await batch.commit();

      // Remove deleted from local state
      const deletedSet = new Set(selectedIds);
      setRegistrations(prev => prev.filter(r => !(r.id && deletedSet.has(r.id))));
      setProgramSpecificRegistrations(prev => prev.filter(r => !(r.id && deletedSet.has(r.id))));
      setFilteredRegistrations(prev => prev.filter(r => !(r.id && deletedSet.has(r.id))));
      setSelectedIds([]);

      // If viewing a specific program, decrement its registeredCount only for
      // registrations that belonged to that program (whether legacy or subcollection)
      if (selectedProgram && selectedProgram !== 'all') {
        const deletionsForProgram = regsToDelete.filter(({ record }) => record && record.programId === selectedProgram).length;
        if (deletionsForProgram > 0) {
          const programRef = doc(db, 'programs', selectedProgram);
          await runTransaction(db, async (transaction) => {
            const pDoc = await transaction.get(programRef);
            if (pDoc.exists()) {
              const current = (pDoc.data() as any).registeredCount || 0;
              transaction.update(programRef, { registeredCount: Math.max(0, current - deletionsForProgram) });
            }
          });
        }
      }

      ui.showMessage('Selected registrations deleted', { type: 'success' });
    } catch (error) {
      console.error('Error deleting registrations:', error);
      ui.showMessage('Failed to delete selected registrations', { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      confirmed: { color: 'bg-green-100 text-green-800', label: 'Confirmed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
      waitlisted: { color: 'bg-blue-100 text-blue-800', label: 'Waitlisted' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getGenderBadge = (gender: string) => {
    const genderConfig: Record<string, { color: string; label: string }> = {
      male: { color: 'bg-blue-100 text-blue-800', label: 'Male' },
      female: { color: 'bg-pink-100 text-pink-800', label: 'Female' },
      other: { color: 'bg-purple-100 text-purple-800', label: 'Other' },
      'prefer-not-to-say': { color: 'bg-gray-100 text-gray-800', label: 'Not Specified' }
    };
    
    const config = genderConfig[gender] || genderConfig['prefer-not-to-say'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getReligionBadge = (religion?: string) => {
    const value = (religion || '').toString().trim().toLowerCase();
    const religionConfig: Record<string, { color: string; label: string }> = {
      christian: { color: 'bg-indigo-100 text-indigo-800', label: 'Christian' },
      muslim: { color: 'bg-emerald-100 text-emerald-800', label: 'Muslim' },
      other: { color: 'bg-gray-100 text-gray-800', label: 'Other' }
    };
    const config = religionConfig[value] || { color: 'bg-gray-100 text-gray-800', label: value ? value.charAt(0).toUpperCase() + value.slice(1) : '-' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getHealthBadge = (health?: string) => {
    if (!health) return <span className="text-xs text-gray-500">-</span>;
    const healthConfig: Record<string, { color: string; label: string }> = {
      good: { color: 'bg-green-100 text-green-800', label: 'Good' },
      fair: { color: 'bg-yellow-100 text-yellow-800', label: 'Fair' },
      poor: { color: 'bg-red-100 text-red-800', label: 'Poor' }
    };
    const config = healthConfig[(health || '').toLowerCase()] || { color: 'bg-gray-100 text-gray-800', label: health };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getMaritalBadge = (marital?: string) => {
    if (!marital) return <span className="text-xs text-gray-500">-</span>;
    const maritalConfig: Record<string, { color: string; label: string }> = {
      single: { color: 'bg-blue-100 text-blue-800', label: 'Single' },
      married: { color: 'bg-green-100 text-green-800', label: 'Married' },
      divorced: { color: 'bg-yellow-100 text-yellow-800', label: 'Divorced' },
      widowed: { color: 'bg-purple-100 text-purple-800', label: 'Widowed' }
    };
    const config = maritalConfig[(marital || '').toLowerCase()] || { color: 'bg-gray-100 text-gray-800', label: marital };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDateOfBirth = (value: unknown): string => {
    if (!value) return '-';

    try {
      if (typeof (value as any)?.toDate === 'function') {
        return (value as any).toDate().toLocaleDateString();
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '-' : value.toLocaleDateString();
      }
      const parsed = new Date(value as any);
      return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  const selectedRegistrationsForTransfer = getSelectedRegistrations().filter(reg => reg.programId === selectedProgram);

  const handleEditRegistration = (registration: ProgramRegistration) => {
    const editRegistrationPayload = {
      id: registration.id,
      beneficiaryId: (registration as any).beneficiaryId || '',
      programId: registration.programId || '',
      programTitle: registration.programTitle || '',
      fullName: registration.fullName || '',
      idNumber: (registration as any).idNumber || '',
      idType: (registration as any).idType || '',
      gender: registration.gender || '',
      religion: (registration as any).religion || '',
      contactNumber: registration.contactNumber || '',
      houseNumber: (registration as any).houseNumber || '',
      address: registration.address || '',
      email: registration.email || '',
      maritalStatus: (registration as any).maritalStatus || '',
      healthStatus: (registration as any).healthStatus || '',
      status: registration.status || 'confirmed',
      dateOfBirth: (registration as any).dateOfBirth || null
    };

    try {
      const editToken = `${Date.now()}-${registration.id || 'new'}`;
      const storageKey = `${EDIT_REGISTRATION_STORAGE_PREFIX}:${editToken}`;
      localStorage.setItem(storageKey, JSON.stringify(editRegistrationPayload));

      const editUrl = `/admin/programs/registrations/add?editToken=${encodeURIComponent(editToken)}`;
      window.open(editUrl, '_blank');
    } catch (error) {
      console.error('Error preparing edit registration in new tab:', error);
      ui.showMessage('Unable to open edit page in a new tab', { type: 'error' });
    }
  };

  const stats = {
    total: filteredRegistrations.length,
    pending: filteredRegistrations.filter(r => r.status === 'pending').length,
    confirmed: filteredRegistrations.filter(r => r.status === 'confirmed').length,
    attended: filteredRegistrations.filter(r => r.attended).length,
    unattended: filteredRegistrations.filter(r => !r.attended).length
  };

  const currentProgramRegistrations = selectedProgram === 'all' ? registrations : programSpecificRegistrations;
  const hasColumnData = (getter: (registration: ProgramRegistration) => unknown): boolean =>
    currentProgramRegistrations.some((registration) => {
      const value = getter(registration);
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      return true;
    });

  const showAddressColumn = hasColumnData((registration) => registration.address);
  const showGenderColumn = hasColumnData((registration) => registration.gender);
  const showDateOfBirthColumn = hasColumnData((registration) => (registration as any).dateOfBirth);
  const showReligionColumn = hasColumnData((registration) => (registration as any).religion);
  const showHealthColumn = hasColumnData((registration) => (registration as any).healthStatus || (registration as any).health);
  const showMaritalColumn = hasColumnData((registration) => (registration as any).maritalStatus || (registration as any).marital);

  return (
    <div className="space-y-2">
      <div className="sticky top-24 z-30 space-y-2 bg-gray-50/95 backdrop-blur-sm pb-1">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-white w-full">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Program Registrations</h2>
          <p className="text-gray-600 text-xs">Manage and track program registrations</p>
        </div>
        
        <div className="flex gap-1">
          {canExport && (
            <button
              onClick={exportToCSV}
              className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
            >
              <FaDownload className="h-4 w-4" /> Export
            </button>
          )}
          {canPrint && (
            <button 
              onClick={() => window.print()}
              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              <FaPrint className="h-4 w-4" /> Print
            </button>
          )}
          {canDelete && (
            <button
              onClick={handleBatchDelete}
              disabled={selectedIds.length === 0 || deleting}
              className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🗑 Delete Selected
            </button>
          )}
          {canEdit && (
            <>
              <button
                onClick={openAddToProgramModal}
                disabled={selectedProgram === 'all' || selectedIds.length === 0}
                className="inline-flex items-center gap-1 px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaPlus className="h-4 w-4" />
                Add To Program
              </button>
              <button
                onClick={openImportModal}
                className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
              >
                <FaPlus className="h-4 w-4" />
                Add From Program
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-1 text-sm">
        <div className="bg-white p-1 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-lg font-bold text-gray-800">{stats.total}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-full">
              <FaUserCheck className="text-blue-600 text-lg" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-1 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Pending</p>
              <p className="text-lg font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-full">
              <FaFilter className="text-yellow-600 text-lg" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-1 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Confirmed</p>
              <p className="text-lg font-bold text-green-600">{stats.confirmed}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-full">
              <FaUserCheck className="text-green-600 text-lg" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-1 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Attended</p>
              <p className="text-lg font-bold text-purple-600">{stats.attended}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-full">
              <FaUserCheck className="text-purple-600 text-lg" />
            </div>
          </div>
        </div>

        <div className="bg-white p-1 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Unattended</p>
              <p className="text-lg font-bold text-gray-600">{stats.unattended}</p>
            </div>
            <div className="p-2 bg-gray-100 rounded-full">
              <FaUserTimes className="text-gray-600 text-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-2 rounded-lg shadow">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2">
          <div className="min-w-0">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="min-w-0">
            <select
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
            >
              <option value="all">All Programs</option>
              {programs.map(program => (
                <option key={program.id} value={program.id}>
                  {program.title}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-0">
            <select
              value={selectedStatus}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="waitlisted">Waitlisted</option>
              <option value="attended">Attended</option>
              <option value="unattended">Unattended</option>
            </select>
          </div>

            <div className="min-w-0">
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
              >
                <option value="all">All Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="min-w-0">
              <select
                value={selectedHealth}
                onChange={(e) => setSelectedHealth(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
              >
                <option value="all">All Health</option>
                <option value="active">Active</option>
                <option value="sick">Sick</option>
                <option value="partially_sick">Partially Sick</option>
                <option value="disabled">Disabled</option>
                <option value="other">Other</option>
              </select>
            </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={bulkCount}
              onChange={(e) => setBulkCount(Math.max(1, Number(e.target.value) || 1))}
              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md"
              title="Number of participants to mark as attended"
            />
            <button
              onClick={async () => {
                // bulk mark handler
                const n = Math.max(1, Math.floor(bulkCount));
                const toMark = filteredRegistrations.filter(r => !r.attended).slice(0, n);
                if (toMark.length === 0) {
                  ui.showMessage('No un-attended participants found', { type: 'info' });
                  return;
                }
                setBulkProcessing(true);
                let successCount = 0;
                let failCount = 0;
                const successIds: string[] = [];
                for (const reg of toMark) {
                  try {
                    if ((reg as any).beneficiaryId && (reg as any).programId) {
                      const res = await programService.markAttendance((reg as any).programId, (reg as any).beneficiaryId);
                      if (res && (res as any).success) {
                        successCount++;
                        if (reg.id) successIds.push(reg.id);
                      } else {
                        failCount++;
                      }
                    } else {
                      const registrationRef = doc(db, 'programRegistrations', reg.id as string);
                      await updateDoc(registrationRef, { attended: true, attendanceDate: serverTimestamp() });
                      successCount++;
                      if (reg.id) successIds.push(reg.id);
                    }
                  } catch (err) {
                    console.error('Bulk mark error for', reg.id, err);
                    failCount++;
                  }
                }

                if (successIds.length > 0) {
                  markAttendanceInLocalState(successIds);
                }

                setBulkProcessing(false);
                ui.showMessage(`Marked ${successCount} attended${failCount ? `, ${failCount} failed` : ''}`, { type: failCount ? 'warning' : 'success' });
              }}
              disabled={bulkProcessing}
              className="px-3 py-1 bg-primary-600 text-white rounded-md text-sm disabled:opacity-50"
              title="Mark number of visible participants as attended"
            >
              {bulkProcessing ? 'Processing...' : 'Mark Attended'}
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Registrations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No registrations found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">#</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {(canDelete || canEdit) ? (
                                <input
                                  type="checkbox"
                                  onChange={selectAllVisible}
                                  checked={paginatedRegistrations.length > 0 && paginatedRegistrations.every(r => selectedIds.includes(r.id as string))}
                                />
                              ) : null}
                  </th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('fullName')}>Registrant {getSortIndicator('fullName')}</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('programTitle')}>Program</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('email')}>Contact {getSortIndicator('email')}</th>
                  {showAddressColumn && (
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('address')}>Address {getSortIndicator('address')}</th>
                  )}
                  {showGenderColumn && (
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('gender')}>Gender {getSortIndicator('gender')}</th>
                  )}
                  {showDateOfBirthColumn && (
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOB</th>
                  )}
                  {showReligionColumn && (
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('religion')}>Religion {getSortIndicator('religion')}</th>
                  )}
                  {showHealthColumn && (
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('healthStatus')}>Health {getSortIndicator('healthStatus')}</th>
                  )}
                  {showMaritalColumn && (
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('maritalStatus')}>Marital {getSortIndicator('maritalStatus')}</th>
                  )}
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('status')}>Status {getSortIndicator('status')}</th>
                  <th
                    className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort(selectedStatus === 'attended' ? 'attendanceDate' : 'registrationDate')}
                  >
                    {selectedStatus === 'attended' ? 'Attended Time' : 'Date'} {getSortIndicator(selectedStatus === 'attended' ? 'attendanceDate' : 'registrationDate')}
                  </th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRegistrations.map((registration, index) => (
                  <tr key={registration.id} className="hover:bg-gray-50">
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-500 font-medium w-8">{(currentPage - 1) * pageSize + index + 1}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {(canDelete || canEdit) ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(registration.id as string)}
                          onChange={() => toggleSelect(registration.id as string)}
                        />
                      ) : null}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-sm text-gray-900">{registration.fullName}</div>
                        {registration.specialRequirements && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            📝 {registration.specialRequirements}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="text-sm text-gray-900 font-medium">
                        {registration.programTitle}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900">{registration.email}</div>
                        <div className="text-sm text-gray-500">{registration.contactNumber}</div>
                        {registration.emergencyContact && (
                          <div className="text-xs text-red-600">
                            🚨 {registration.emergencyContact}
                          </div>
                        )}
                      </div>
                    </td>
                    {showAddressColumn && (
                      <td className="px-2 py-1 text-sm text-gray-700 max-w-[170px] truncate">{registration.address || '-'}</td>
                    )}
                    {showGenderColumn && (
                      <td className="px-2 py-1 whitespace-nowrap">{getGenderBadge((registration as any).gender)}</td>
                    )}
                    {showDateOfBirthColumn && (
                      <td className="px-2 py-1 whitespace-nowrap text-sm text-gray-700">
                        {formatDateOfBirth((registration as any).dateOfBirth)}
                      </td>
                    )}
                    {showReligionColumn && (
                      <td className="px-2 py-1 whitespace-nowrap">{getReligionBadge((registration as any).religion)}</td>
                    )}
                    {showHealthColumn && (
                      <td className="px-2 py-1 whitespace-nowrap">{getHealthBadge((registration as any).healthStatus || (registration as any).health)}</td>
                    )}
                    {showMaritalColumn && (
                      <td className="px-2 py-1 whitespace-nowrap">{getMaritalBadge((registration as any).maritalStatus || (registration as any).marital)}</td>
                    )}
                    <td className="px-2 py-1 whitespace-nowrap">{getStatusBadge(registration.status)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(selectedStatus === 'attended'
                          ? registration.attendanceDate?.toDate?.()?.toLocaleDateString()
                          : registration.registrationDate?.toDate?.()?.toLocaleDateString()) || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(selectedStatus === 'attended'
                          ? registration.attendanceDate?.toDate?.()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : registration.registrationDate?.toDate?.()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })) || ''}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {canEdit && (
                            <div className="flex gap-1 items-center">
                            {registration.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => registration.id && handleMarkAttendance(registration as any)}
                                  className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200"
                                  title="Mark Attended"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => registration.id && updateRegistrationStatus(registration.id, 'cancelled')}
                                  className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200"
                                  title="Cancel"
                                >
                                  ✕
                                </button>
                              </>
                            )}

                            {registration.status === 'confirmed' && !registration.attended && (
                              <button
                                onClick={() => handleMarkAttendance(registration as any)}
                                disabled={updatingAttendance === registration.id}
                                className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded hover:bg-purple-200 disabled:opacity-50"
                                title="Mark Attended"
                              >
                                {updatingAttendance === registration.id ? '...' : '✓ Attended'}
                              </button>
                            )}
                          </div>
                        )}
                        
                        <div className="flex gap-2 items-center">
                          {canEdit && (
                            <button
                              onClick={() => handleEditRegistration(registration)}
                              className="text-amber-600 hover:text-amber-800 text-sm"
                              title="Edit Registrant"
                            >
                              <FaEdit />
                            </button>
                          )}
                          <button
                            onClick={() => { window.location.href = `mailto:${registration.email}`; }}
                            className="text-blue-600 hover:text-blue-900 text-sm"
                            title="Send Email"
                          >
                            <FaEnvelope />
                          </button>
                          
                          {registration.attended ? (
                            <span className="text-green-600" title="Attended"><FaUserCheck /></span>
                          ) : (
                            <span className="text-gray-400" title="Not Attended"><FaUserTimes /></span>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Import Participants From Another Program</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowImportModal(false)} className="text-gray-500 hover:text-gray-800">Cancel</button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="md:col-span-2">
                <select
                  value={sourceProgramId}
                  onChange={(e) => setSourceProgramId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select source program</option>
                  {programs.filter(p => p.id !== selectedProgram).map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    setSelectedSourceIds(sourceRegistrations.map(r => r.id!).filter(Boolean));
                  }}
                  className="px-3 py-2 bg-gray-100 rounded-md"
                >Select All</button>
                <button
                  onClick={() => setSelectedSourceIds([])}
                  className="px-3 py-2 bg-gray-100 rounded-md"
                >Clear</button>
              </div>
            </div>

            <div className="max-h-72 overflow-auto border rounded-md p-2 mb-3">
              {loadingSourceRegs ? (
                <div className="text-center py-6">Loading...</div>
              ) : sourceRegistrations.length === 0 ? (
                <div className="text-center py-6 text-sm text-gray-500">No participants found in source program.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="p-2"> </th>
                      <th className="p-2">Name</th>
                      <th className="p-2">Email</th>
                      <th className="p-2">Phone</th>
                      <th className="p-2">Religion</th>
                      <th className="p-2">ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceRegistrations.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="p-2 w-8">
                          <input type="checkbox" checked={selectedSourceIds.includes(r.id!)} onChange={() => toggleSelectSource(r.id!)} />
                        </td>
                        <td className="p-2">{r.fullName}</td>
                        <td className="p-2">{r.email}</td>
                        <td className="p-2">{r.contactNumber}</td>
                        <td className="p-2">{(r as any).religion || '-'}</td>
                        <td className="p-2">{r.idNumber || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setShowImportModal(false)} className="px-3 py-2 rounded-md border">Close</button>
              <button
                onClick={handleImportSelected}
                disabled={importLoading || selectedSourceIds.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md disabled:opacity-50"
              >
                {importLoading ? 'Importing...' : `Add ${selectedSourceIds.length || ''} to Program`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add To Program Modal */}
      {showAddToProgramModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Add Selected Members To Another Program</h3>
              <button onClick={closeAddToProgramModal} className="text-gray-500 hover:text-gray-800">Cancel</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination Program</label>
                <select
                  value={targetProgramId}
                  onChange={(e) => setTargetProgramId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select destination program</option>
                  {programs.filter(p => p.id !== selectedProgram).map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>
              <div className="bg-gray-50 border rounded-md p-3 text-sm">
                <p className="text-gray-500 text-xs">Source Program</p>
                <p className="font-medium">{programs.find(p => p.id === selectedProgram)?.title || '-'}</p>
                <p className="text-gray-500 text-xs mt-2">Selected Members</p>
                <p className="font-medium">{selectedRegistrationsForTransfer.length}</p>
              </div>
            </div>

            <div className="max-h-72 overflow-auto border rounded-md p-2 mb-3">
              {selectedRegistrationsForTransfer.length === 0 ? (
                <div className="text-center py-6 text-sm text-gray-500">No selected members to transfer.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="p-2">Name</th>
                      <th className="p-2">Contact</th>
                      <th className="p-2">Address</th>
                      <th className="p-2">Email</th>
                      <th className="p-2">Religion</th>
                      <th className="p-2">ID</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRegistrationsForTransfer.map((reg) => (
                      <tr key={reg.id} className="border-t">
                        <td className="p-2">{reg.fullName || '-'}</td>
                        <td className="p-2">{reg.contactNumber || '-'}</td>
                        <td className="p-2">{reg.address || '-'}</td>
                        <td className="p-2">{reg.email || '-'}</td>
                        <td className="p-2">{(reg as any).religion || '-'}</td>
                        <td className="p-2">{reg.idNumber || '-'}</td>
                        <td className="p-2 capitalize">{reg.status || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button onClick={closeAddToProgramModal} className="px-3 py-2 rounded-md border">Close</button>
              <button
                onClick={handleAddSelectedToProgram}
                disabled={addingToProgram || !targetProgramId || selectedRegistrationsForTransfer.length === 0}
                className="px-4 py-2 bg-teal-600 text-white rounded-md disabled:opacity-50"
              >
                {addingToProgram ? 'Adding...' : `Add ${selectedRegistrationsForTransfer.length || ''} to Program`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center bg-white p-2 rounded-lg shadow">
        <div className="text-xs text-gray-700">
          Showing <span className="font-medium">{showingStart}{showingEnd ? `-${showingEnd}` : ''}</span> of <span className="font-medium">{filteredRegistrations.length}</span>
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 border border-gray-300 rounded-md text-xs hover:bg-gray-50 disabled:opacity-50"
          >Prev</button>
          <div className="text-xs">Page <strong>{currentPage}</strong> / {totalPages}</div>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 border border-gray-300 rounded-md text-xs hover:bg-gray-50 disabled:opacity-50"
          >Next</button>
        </div>
      </div>
    </div>
  );
};

export default ProgramRegistrations;
